import type { ActionHandlerMap, ActionParams } from "./types";

import { jobSyncQueryKeys } from "@/features/integrations/modules/job-sync/api/query-keys";
import { instance } from "@/lib/axios/instance";

/**
 * Werkzoeken-specific action handlers for job-sync module.
 */
export const werkzoekenActions: ActionHandlerMap = {
  reregisterFeed: async ({ clientId, queryClient, onSuccess, onError }: ActionParams) => {
    if (!clientId) {
      onError?.(new Error("ClientId is required"));
      return;
    }

    // Optimistically reset queries before API call to clear stale data immediately
    const feedRegistrationKey = jobSyncQueryKeys.feedRegistration("werkzoeken", clientId);
    const feedComparisonKey = jobSyncQueryKeys.feedComparison("werkzoeken", clientId);
    const feedContentPrefix = jobSyncQueryKeys.feedContent("werkzoeken");

    // Reset queries to clear stale data immediately (optimistic update)
    queryClient?.resetQueries({
      queryKey: feedRegistrationKey,
    });
    // Set comparison query to a loading placeholder to show seamless loading state
    // This prevents showing stale mismatch data and prevents auto-refetch
    // The placeholder is recognized by the UI as a loading state
    // Polling will handle the actual fetch after a delay
    queryClient?.setQueryData(feedComparisonKey, {
      __isLoadingPlaceholder: true,
      isMismatched: false,
      isFeedUrlMismatched: false,
      werkzoekenFeedId: null,
      werkzoekenFeedUrl: null,
    });
    // Reset all feed-content queries (they depend on feedUrl which may change)
    queryClient?.resetQueries({
      queryKey: feedContentPrefix,
      exact: false, // Match all queries starting with this prefix
    });

    try {
      await instance.post(
        `/api/v2/Integrations/werkzoeken/job-sync/feed-registration/reregister`,
        {},
        {
          params: {
            clientId,
          },
        }
      );
      
      // Invalidate and refetch queries after successful reregister to get fresh data
      // Invalidate first to ensure we bypass staleTime and get truly fresh data
      queryClient?.invalidateQueries({
        queryKey: feedRegistrationKey,
        refetchType: "active",
      });
      queryClient?.invalidateQueries({
        queryKey: feedContentPrefix,
        refetchType: "active",
        exact: false, // Match all queries starting with this prefix
      });
      
      // Poll comparison query until it matches the new registration feedId
      // Werkzoeken's external API may take time to reflect the new registration
      // Use fixed 2-second intervals for simplicity (up to 5 attempts = 10 seconds)
      setTimeout(async () => {
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            // Wait 2 seconds between attempts (except before first attempt)
            if (attempt > 0) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
            
            // Refetch comparison query
            await queryClient?.refetchQueries({
              queryKey: feedComparisonKey,
              type: "active",
            });
            
            // Check if comparison data matches registration data
            const registrationData = queryClient?.getQueryData<{ feedId?: string }>(feedRegistrationKey);
            const comparisonData = queryClient?.getQueryData<{ werkzoekenFeedId?: string | null }>(feedComparisonKey);
            
            const registrationFeedId = registrationData?.feedId;
            const werkzoekenFeedId = comparisonData?.werkzoekenFeedId;
            
            // If feedIds match, we're done
            if (registrationFeedId && werkzoekenFeedId && registrationFeedId === werkzoekenFeedId) {
              return;
            }
          } catch {
            // On error, continue polling - final invalidation happens after loop
          }
        }
        
        // Final invalidation even if polling didn't find a match
        queryClient?.invalidateQueries({
          queryKey: feedComparisonKey,
          refetchType: "active",
        });
      }, 1000); // Initial 2 second delay to allow Werkzoeken backend to update
      
      onSuccess?.();
    } catch (error) {
      // On error, invalidate queries to ensure UI reflects error state
      queryClient?.invalidateQueries({
        queryKey: feedRegistrationKey,
        refetchType: "active",
      });
      queryClient?.invalidateQueries({
        queryKey: feedComparisonKey,
        refetchType: "active",
      });
      
      // Preserve the original error structure (especially AxiosError with response.data.detail)
      // so that error handlers can extract ProblemDetails detail field
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  },
};
